// Dependencies
import React, { Fragment, useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRealtimeUnits, useRealtimeTags } from 'Core/hooks';
import { useIntl } from 'react-intl';
import { Container, Grid, Typography } from '@mui/material';
import { validateJsonObject } from 'Core/data/Helpers';

// Components
import { Pill, Select } from 'Components';
import { FilterButton } from 'Components/Table';
import SidePanel, { SidePanelHeader, SidePanelBody, SidePanelFooter } from 'Components/SidePanel';
import { MenuComponent } from 'Components/Menu';
import { Table, SearchInput, Toolbar, Button } from 'Components/uiControls';
import { TableButtons } from 'Components/uiControls/Table';
import { usePagination } from 'Components/uiControls/Table/hooks';
import SeverityOptions from 'Modules/alerts/common/components/SeverityOptions';
import WizardAlert from 'Modules/alerts/components/wizardAlert';
import DeleteTab from './.components/deleteTab';
import AssignedModal from './.components/assignedModal';
import { NotificationManager } from 'Components/Notifications';
import { TagsGroup } from 'Components/Tag';
import useExport from "Components/Table/Export";


// Helpers
import { buildAlertSummary } from '../../../../common/helpers/alertSummaryField';
import alertTypes from 'Modules/alerts/common/alertTypes.json';

// Core
import { getTagColor } from 'Core/data/Helpers';
import { HasPermission } from "Core/security"

// Actions
import {
  alertsOpenFilterModal,
  searchAlertsTab,
  alertsWizardOpen,
  sendGenericExport,
  clearRedux,
  alertsOpenDeleteModal,
  alertsOpenAssignedModal
} from 'Redux/actions';

// Styles
import colors from 'Assets/sass/core/vars.scss';
import './style.scss';
import TagsManage from '../../../../../.common/components/TagsManage';
import en from '../../../../../routes/views/templates/lan/en';


export const AlersTab = (props) => {

  const dispatch = useDispatch();
  const tableRef = useRef();
  const intl = useIntl()
  const { messages, locale } = intl;

  // local states
  const [showFilters, setShowFilters] = useState(false);
  const [deletingAlertData, setDeletingAlertData] = useState({ title: '', contentText: '', confirmText: '', cancelText: '', visible: false, });
  const [filters, setFilters] = useState({})
  const [allFilters, setAllFilters] = useState({})
  const [filtersByExport, setFiltersByExport] = useState({})
  const [entity, setEntity] = useState({ id: "All", label: messages['clipRequestAdvancedFilters_all'] });
  const [alertsSupported, setAlertsSupported] = useState([])
  const [refreshSuscriber, setRefreshSuscriber] = useState(false)
  const [loadingPills, setLoadingPills] = useState(false)
  const [selectedEntity, setSelectedEntity] = useState({ id: "All", label: messages['clipRequestAdvancedFilters_all'] });


  // redux states
  const { alerts: data, filters: filtersPag, total, loading, isOpenModalDeleteAlerts: open, isOpenModalAssigned } = useSelector((state) => state.alertsTabRedux);
  const { alertTypesSupported } = useSelector((state) => state.alertsWizardRedux);
  const exportResult = useSelector(state => state.commomRedux.exportResult);
  const user = useSelector((state) => state.securityRedux.user);
  const contacts = useSelector((state) => state.catalogsRedux.contacts);
  const loadingTags = useSelector((state) => state.catalogsRedux.loadingTags);
  const units = useRealtimeUnits({ trackFields: ['id', 'name', 'label', 'disabled', 'driverId', 'vehicleStatusId', 'tags'] });
  const tags = useRealtimeTags({ trackFields: ['id', 'label', 'disabled', 'tagPath', 'tags'] });

  const unitToExport = useRealtimeUnits({ trackFields: ['id', 'name', 'label', 'disabled', 'driverId', 'vehicleStatusId', 'tags'] });
  const pagination = usePagination(tableRef, searchAlertsTab, filtersPag);


  // alert options
  const menuAlertsOptions = [messages['alertsSideMenu_buttonEdit']];

  useEffect(() => {
    if (exportResult) {
      NotificationManager.success(
        messages['exportInProcessBody'],
        messages['success'],
        4000,
        null,
        null,
        'info',
        null
      );
      dispatch(clearRedux("COMMON", { exportResult: null }));
    }
  }, [exportResult]);

  useEffect(() => {
    tableRef?.current?.setPage(1);
    //tableRef?.current?.setRowsPerPage(10);
    pagination.setTotal(total || 0);
  }, [total]);

  useEffect(() => {
    if (data?.length > 0 && refreshSuscriber) {
      setRefreshSuscriber(false)
      pagination?.refresh()
      tableRef?.current?.setPage(tableRef?.current?.page);
    }
  }, [data]);

  useEffect(()=>{
    if(locale){
      tableRef?.current?.setPage(1);
      tableRef?.current?.setSelectedItems([]);
      pagination?.setSelectedItems([]);
    }
  },[locale])

  //change locales
  useEffect(() => {
      pagination.setColumns([
        {
          key: 'name',
          sort: true,
          show: true,
          title: messages['alertsTabView_label'],
          export: true,
          render: (alert) => {
            return (
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                className="context-menu-container"
              >
                <Grid item xs zeroMinWidth>
                  <Typography noWrap>{alert?.name}</Typography>
                </Grid>
                {
                  (HasPermission(user, '', [7]) && alertsSupported?.find(x => x.code === alert.type)) &&
                  <Grid item>
                    <MenuComponent
                      options={menuAlertsOptions}
                      icon="more_vert"
                      className="context-menu"
                      onClick={(type, value) => {
                        if (type === "Edit" || type === "Editar") handleEditAlert(value)
                      }}
                      value={alert}
                    />
                  </Grid>
                }
              </Grid>
            )
          },
          functionString: 'return row.name;'
        },
        {
          key: 'type',
          sort: true,
          show: true,
          title: messages['alertsTabView_action'],
          export: true,
          render: (alert) => {
            return (
              <Grid item sm={12}>{inputName[alert.type] || alert.type}</Grid>
            )
          },
          functionString: 'return row.type;'
        },
        {
          key: 'buildSummary',
          sort: false,
          show: true,
          title: messages['alertsTabView_summary'],
          export: true,
          render: (alert) => {
            return (
              <Grid item sm={12}>{buildAlertSummary(alert, intl, user) || ''}</Grid>
            )
          },
          functionString: `return row?.buildSummary;`
        },
        {
          key: 'assigned',
          sort: false,
          show: true,
          title: messages['alertsTabView_assigned'],
          export: true,
          render: (alert) => {
            let assignedUnits = alert?.assignedTo?.filter(to => to?.entity?.name == "Unit" || to?.entity?.name == "All");
            let assignedDrivers = alert?.assignedTo?.filter(to => to?.entity?.name == "Driver");
            let assignedTags = alert?.assignedTo?.filter(to => to?.entity?.name == "Tag");

            if (assignedUnits?.find(assignedUnit => assignedUnit?.entity?.id == -1)) {
              return (
                <span
                  className='btn-link rectangle-assigment'
                  onClick={() => {
                    dispatch(alertsOpenAssignedModal(true, alert, 1, 0));
                  }}
                >
                  {messages['alertsTabView_all']}
                </span>
              );
            } else if (assignedDrivers?.find(assignedDriver => assignedDriver?.entity?.id == -1)) {
              return (
                <span
                  className='btn-link rectangle-assigment'
                  onClick={() => {
                    dispatch(alertsOpenAssignedModal(true, alert, 2, 0));
                  }}
                >
                  {messages['alertsTabView_allDriversSelected']}
                </span>
              );
            }

            let selectedTags = [];
            if (!assignedUnits?.length && !assignedDrivers?.length && assignedTags?.length) {
              let tagIds = assignedTags.map(assignedTag => parseInt(assignedTag?.entity?.id));
              selectedTags = tags.filter(tag => tagIds?.includes(parseInt(tag?.id)));
            }

            return (
              <Grid item sm={12}>
                {
                  selectedTags?.length ?
                    <div
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        dispatch(alertsOpenAssignedModal(true, alert, 1, 1));
                      }}
                    >
                      <TagsGroup
                        options={(selectedTags || [])?.map(selectedTag => {
                          return {
                            ...selectedTag,
                            color: `#${getTagColor(selectedTag?.color)}`
                          }
                        })}
                        nameField="label"
                        colorField="color"
                      />
                    </div> :
                    <span className='rectangle-assigment'>
                      {
                        assignedUnits?.length > 0 &&
                        <span
                          className='btn-link'
                          onClick={() => {
                            dispatch(alertsOpenAssignedModal(true, alert, 1, 0));
                          }}
                        >
                          {`${assignedUnits?.length} ${assignedUnits?.length > 1 ? messages['alertsTabView_units'] : messages['alertsTabView_unit']}`}

                          {
                            assignedTags?.length > 0 && ", "
                          }
                        </span>
                      }

                      {
                        assignedDrivers?.length > 0 &&
                        <span
                          className='btn-link'
                          onClick={() => {
                            dispatch(alertsOpenAssignedModal(true, alert, 2, 0));
                          }}
                        >
                          {`${assignedDrivers?.length} ${assignedDrivers?.length > 1 ? messages['alertsTabView_drivers'] : messages['alertsTabView_driver']}`}

                          {
                            assignedTags?.length > 0 && ", "
                          }
                        </span>
                      }

                      {
                        assignedTags?.length > 0 &&
                        <span
                          className='btn-link'
                          onClick={() => {
                            dispatch(alertsOpenAssignedModal(true, alert, 1, 1));
                          }}
                        >
                          {`${assignedTags?.length} ${assignedTags?.length > 1 ? messages['alertsTabView_tags'] : messages['alertsTabView_tag']}`}
                        </span>
                      }
                    </span>

                }
              </Grid>
            )
          },
          functionString: 'return row?.unitName;'
        },
        {
          key: 'severity',
          sort: true,
          show: true,
          title: messages['alertsTabView_severity'],
          export: true,
          render: (alert) => {
            return (
              <Grid item sm={12}>{severityPill(alert)}</Grid>
            )
          },
          functionString: 'return row.severity;'
        },
        {
          key: 'subscribers',
          sort: false,
          show: true,
          title: messages['alertsTabView_subscribers'],
          export: true,
          render: (alert) => {
            const subscribers = alert?.subscribers;
            if (subscribers) {
              if (subscribers?.find(subscriber => subscriber?.contactId == -1) && subscribers?.find(subscriber => subscriber?.contactId == -2)) {
                return (
                  <span
                    className='btn-link rectangle-assigment'
                    onClick={() => {
                      dispatch(alertsOpenAssignedModal(true, alert, 0));
                    }}
                  >
                    {messages['alertsTabView_allContacts']}, {messages['alertsTabView_currentDriver']}
                  </span>
                );
              } else if (subscribers?.find(subscriber => subscriber?.contactId == -1)) {
                return (
                  <span
                    className='btn-link rectangle-assigment'
                    onClick={() => {
                      dispatch(alertsOpenAssignedModal(true, alert, 0));
                    }}
                  >
                    {messages['alertsTabView_allContacts']}
                  </span>
                );
              }
            }

            return (
              <Fragment>
                {
                  alert.subscribersCount > 0 &&
                  <span
                    className='btn-link rectangle-assigment'
                    onClick={() => {
                      dispatch(alertsOpenAssignedModal(true, alert, 0));
                    }}
                  >
                    {`${alert.subscribersCount} ${alert.subscribersCount > 1 ? messages['alertsTabView_contacts'] : messages['alertsTabView_contact']}`}
                  </span>
                }
              </Fragment>
            )
          },
          functionString: 'return row?.subscribers?.map(item => { if(item.contactId === -1){return "All contacts"}else if(item.contactId === -2){return "Current Driver"}else { return item?.contact?.title ? item.contact.title : "No name"} }).join(",");'
        },
      ])

  }, [locale, user, tags?.length, alertsSupported])

  useEffect(() => {
    pagination.onChangePage();
  }, [locale])

  useEffect(() => {
    if (loading) {
      tableRef?.current?.setSelectedItems([]);
      pagination?.setSelectedItems([]);
    }
  }, [loading])

  useEffect(() => {
    if (alertTypesSupported && alertsSupported.length === 0) {

      let supported = [];
      supported = alertTypesSupported?.filter((alert) =>
        alert.parentId == null && alertsSupported.includes(alert.code)
      );

      let validationQueclink = units?.some((item) => (item?.unitVersionProduct === "GL500MG"))//&& process.env.ENVIROMENT === "production"

      if (!validationQueclink) {
        supported = supported?.filter((alert) => alert?.code !== "OnEvent");
      }

      alertTypes.map((alert) => supported.push(alert));
      let hiddenAlerts = ["OBDIIAlerts", "OBDII", "Geofence"]; // "FuelCard","HeavyDutyDTC", "LightDutyDTC", 
      if (!HasPermission(user, "mediaalerts", "212")) {
        hiddenAlerts.push("Media")
      }
      if (!HasPermission(user, "fuel", [49])) {
        hiddenAlerts.push("FuelCard");
      }

      if (!HasPermission(user, "pFuelLev", [291])) {
        hiddenAlerts.push("FuelLevel");
      }

      supported = supported.filter(s => !hiddenAlerts.includes(s.code))

      supported = supported.sort((a, b) => {
        if (a.name?.toString()?.toLowerCase() > b.name?.toString()?.toLowerCase()) return 1;
        if (b.name?.toString()?.toLowerCase() > a.name?.toString()?.toLowerCase()) return -1;
        return 0;
      });
      setAlertsSupported(supported);
    }
  }, [alertTypesSupported])

  useEffect(() => {
    setAllFilters({
      ...filters,
      severities: filters?.severities?.map(x => x.id),
      assignedTo: filters?.assignedTo?.map(x => { return { id: x.id, name: x.name, label: x.label } }),
      types: filters?.types?.map(x => x.code)
    })
  }, [filters]);

  // language
  const {
    alertsTabView_stoppedReporting,
    alertsTabView_vehicleCongregation,
    alertsTabView_driverBehavior,
    alertsTabView_consistentSpeed,
    alertsTabView_lightDutyDTC,
    alertsTabView_offHour,
    alertsTabView_heavyDutyDTC,
    alertsTabView_fuelCard,
    alertsTabView_onEvent,
    alertsTabView_unassignedDriver,
    alertsTabView_smartOneCLowBattery,
    alertsTabView_OBDII,
    alertsTabView_totalDriveTime,
    alertsTabView_ConsecutiveDriving,
    alertsTabView_cameraEvent,
    alertsTabView_lowBattery,
    alertsTabView_installSensor,
    alertsTabView_userUpdate,
    alertsTabView_tamperSensor,
    alertsTabView_lightSensor,
    alertsTabView_idle,
    alertsTabView_speed,
    alertsTabView_fsmMealBreak,
    alertsNewAlert_ELD150MilesRule,
    alertsTabView_route_watchers,
    alertsTabView_fuelLevel,
    alertsTabView_warning,
    alertsTabView_stop,
    alertsTabView_stationary,
    alertsTabView_landmark,
    alertsTabView_tow,
    alertsTabView_routes,
    alertsTabView_emissionCheck
  } = messages;

  const inputName = {
    StoppedReporting: alertsTabView_stoppedReporting,
    VehicleCongregation: alertsTabView_vehicleCongregation,
    DriverBehavior: alertsTabView_driverBehavior,
    ConsistentSpeed: alertsTabView_consistentSpeed,
    LightDutyDTC: alertsTabView_lightDutyDTC,
    OffHour: alertsTabView_offHour,
    HeavyDutyDTC: alertsTabView_heavyDutyDTC,
    FuelCard: alertsTabView_fuelCard,
    OnEvent: alertsTabView_onEvent,
    UnassignedDriver: alertsTabView_unassignedDriver,
    SmartOneCLowBattery: alertsTabView_smartOneCLowBattery,
    OBDII: alertsTabView_OBDII,
    TotalDriveTime: alertsTabView_totalDriveTime,
    ConsecutiveDriving: alertsTabView_ConsecutiveDriving,
    CameraEvent: alertsTabView_cameraEvent,
    LowBattery: alertsTabView_lowBattery,
    InstallSensor: alertsTabView_installSensor,
    UserUpdate: alertsTabView_userUpdate,
    TamperSensor: alertsTabView_tamperSensor,
    LightSensor: alertsTabView_lightSensor,
    Idle: alertsTabView_idle,
    Speed: alertsTabView_speed,
    FSMMealBreak: alertsTabView_fsmMealBreak,
    ELD150MilesRule: alertsNewAlert_ELD150MilesRule,
    RouteWatchers: alertsTabView_route_watchers,
    FuelLevel: alertsTabView_fuelLevel,
    Warning: alertsTabView_warning,
    Stop: alertsTabView_stop,
    Stationary: alertsTabView_stationary,
    Landmark: alertsTabView_landmark,
    Tow: alertsTabView_tow,
    Routes: alertsTabView_routes,
    EmissionCheck: alertsTabView_emissionCheck
  };

  const {
    title,
    contentText,
    confirmText,
    cancelText,
    visible,
  } = deletingAlertData;

  // functions
  const severityPill = ({ severity }) => {
    const severityOption = {
      Low: { color: colors.green, label: messages["alertsNewAlert_LowTitle"] },
      Medium: { color: colors.orange, label: messages["alertsNewAlert_MediumTitle"] },
      High: { color: colors.red, label: messages["alertsNewAlert_HighTitle"] },
    };
    return (
      <Pill
        textField="name"
        item={{
          name: severityOption[severity].label,
          color: severityOption[severity].color,
        }}
        style={{ width: '75px' }}
      />
    )
  };

  const handleEditAlert = (value) => {
    let configJSON = validateJsonObject(value?.configJSON);
    setShowFilters(false)
    dispatch(alertsOpenFilterModal(false));
    dispatch(alertsWizardOpen({ ...value, event: configJSON?.cameraEvents }))
  }

  const entities = [
    { id: "All", label: messages['clipRequestAdvancedFilters_all'] },
    { id: "Unit", label: messages['clipRequestAdvancedFilters_singleUnits'] },
    { id: "Driver", label: messages['clipRequestAdvancedFilters_drivers'] }
  ]

  const getData = useCallback((newFilters = filters) => {
    tableRef?.current?.setPage(1);
    tableRef?.current?.setSelectedItems([]);
    pagination?.setSelectedItems([]);
    pagination.refresh(
      {
        ...newFilters,
        severities: newFilters?.severities?.map(x => x.id),
        assignedTo: newFilters?.assignedTo?.map(x => { return { id: x.id, name: x.name, label: x.label } }),
        types: newFilters?.types?.map(x => x.code)
      }
    )
  });

  const onChangeEntityTagPicker = useCallback((items = []) => {
    const newItems = Array.isArray(items) ? items : [];

    setFilters(prevFilters => ({
      ...prevFilters,
      assignedTo: newItems.map(n => {
        if (n.name === "Tag") {
          return { ...n, label: entity?.id };
        }
        return n;
      })
    }));
  }, [entity]);

  //export methods when user selected items
  const getHeaders = () => {
    return [
      messages['alertsTabView_label'],
      messages['alertsTabView_action'],
      messages['alertsTabView_summary'],
      messages['alertsTabView_assigned'],
      messages['alertsTabView_severity'],
      messages['alertsTabView_subscribers']
    ];
  };

  const getDataToExport = (rows) => {
    let data = [];
    rows.forEach((row) => {
      const name = row?.name
      const type = inputName[row?.type] ?  inputName[row?.type] : row?.type
      const summary = buildAlertSummary(row, intl, user) ? buildAlertSummary(row, intl, user) : row?.buildSummary
      const severity = row?.severity
      const getSubscribers = []
      row?.subscribers?.length > 0 &&
        row?.subscribers?.map(subscriberItem => {
          if(subscriberItem?.contactId === -1){
            getSubscribers.push(messages['alertsTabView_allContacts'])
          }else if(subscriberItem?.contactId === -2){
            getSubscribers.push(messages['alertsTabView_currentDriver'])
          }else{
            const findContact = contacts?.find(contact => contact?.id === subscriberItem?.contactId )
            if(findContact?.title){
              getSubscribers.push(findContact?.title)
            }
          }
        })
      const splitSubscribers = getSubscribers?.join(",")
      row?.assignedTo?.length > 0 &&
        row?.assignedTo?.map(assigned => {
          if(assigned?.entity?.id === "-1"){
            units?.length > 0 &&
              units?.map(unit => {
                const unitAssigned = unit?.label
                data.push([name, type, summary, unitAssigned, severity, splitSubscribers]);
              })
        }else if(assigned?.entity?.name === 'Tag'){
            unitToExport?.length > 0 &&
              unitToExport?.map(item => {
              if(item?.tags?.length > 0){
                  const findUnit = item?.tags?.find(tag => tag?.id == assigned?.entity?.id)
                if(findUnit?.id){
                    const unitAssigned = item?.label
                    data.push([name, type, summary, unitAssigned, severity, splitSubscribers])
                  }
                }
              })
        }else if(assigned?.entity?.name === 'Unit'){
            const findUnit = units?.find(unit => unit?.id == assigned?.entity?.id)
            let unitAssigned = ''
            if(findUnit?.label){
              unitAssigned = findUnit?.label
            }
            data.push([name, type, summary, unitAssigned, severity, splitSubscribers])
          }
        })
    });
    return data;
  };

  const exportCSVPdf = useExport(
    getHeaders,
    getDataToExport,
    null,
    pagination?.selectedItems || [],
    null,
    messages["role_alerts"],
    null,
    true
  );

  return (
    <Container maxWidth={false} className="container-view">
      <Grid container direction="row" justifyContent="space-between" alignItems="center">
        <Grid item xs={6} sm={4}>
          <Toolbar>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
            >
              {
                HasPermission(user, '', [6]) &&
                <Grid item>
                  <div style={{ marginLeft: 6 }}>
                    <Button
                      type="icon"
                      icon="add_box"
                      placement="bottom"
                      tooltip={messages['add']}
                      onClick={(e) => {
                        dispatch(alertsOpenFilterModal(false));
                        setShowFilters(false)
                        dispatch(alertsWizardOpen({}))
                      }}
                    />
                  </div>
                </Grid>
              }

              <Grid item>
                <FilterButton
                  tooltip={messages["alertsTabView_FilterLabel"]}
                  onClick={() => {
                    setShowFilters(!showFilters)
                  }}
                />
              </Grid>
              <Grid item>
                <Button
                  type="icon"
                  icon="delete"
                  placement="bottom"
                  tooltip={messages['delete']}
                  disabled={pagination?.selectedItems?.length == 0}
                  onClick={(e) => {
                    dispatch(alertsOpenDeleteModal(true, pagination?.selectedItems));
                  }}
                />
              </Grid>
              <Grid item>
                <TableButtons messages={messages} tableRef={tableRef} buttons={['export']}
                  {...pagination}
                  onExport={(type) => {
                    if (pagination?.selectedItems?.length > 0) {
                      if (type === "Csv") {
                        exportCSVPdf.exportCSV();
                      } else if (type === "Pdf") {
                        exportCSVPdf.exportPDF();
                      }
                    } else{
                      const columns = tableRef.current?.columns
                      const headers = columns.filter(x => x.show && x.export).map(x => { return { header: x.title, column: x.key, functionString: x.functionString } }) || [];
                      dispatch(sendGenericExport(
                        "alerts",
                        type,
                        `
                        query ($openSearch: String, $types: [enumAlertType], $assignedTo: [EntityInput], $severities: [Severity], $limit: Int, $offset: Int) {
                          Services: systemAlertsExport(
                              openSearch: $openSearch,
                              types: $types,
                              assignedTo: $assignedTo,
                              severities: $severities,
                              limit: $limit,
                              offset: $offset
                            ) {
                              items {
                                id
                                name
                                type
                                severity
                                triggerCount
                                assignmentsCount
                                summary
                                buildSummary
                                configJSON
                                assignedTo {
                                    id
                                    entity {
                                        name
                                        id
                                    }
                                }
                                subscribers {
                                  id
                                  contactId
                                  contact {
                                    title
                                  }
                                }
                                subscribersCount
                                unitName
                              },
                              total
                            }
                        }
                        `,
                        {
                          ...pagination.filters,
                          ...filtersByExport,
                          limit: 500,
                          offset: 0
                        },
                        headers,
                        { unit: 'pt', size: 'A4', orientation: 'landscape', fontSize: 10 }
                      ))
                    }}
                  }
                />
              </Grid>
            </Grid>
          </Toolbar>
        </Grid>
        <Grid item xs={12} sm={4} className="mb-10 text-right">
          <SearchInput
            placeholder={messages['search']}
            {...pagination}
          />
        </Grid>
      </Grid>

      <Table
        ref={tableRef}
        keyColumn="id"
        loading={loading || loadingTags}
        topheight={300}
        data={data}
        exportOrientation='landscape'
        exportName='alerts'
        {...pagination}
      />

      <SidePanel
        isOpen={showFilters}
        toggle={() => {
          setShowFilters(!showFilters)

        }}
        style={{ width: '350px', transform: 'translateX(350px)' }}
      >
        <SidePanelHeader>
          <Grid container direction='row' justifyContent='space-between' alignItems='center'>
            <Grid item>
              <label className="advanced-filters-title ">{messages['alertsFiltersMenu_menuTitle']}</label>
            </Grid>

            <Grid item className='text-right'>
              <Button
                type='icon'
                icon='delete'
                tooltip={messages['alertsFiltersMenu_clearFilters']}
                placement='left'
                onClick={() => {
                  setFilters({})
                  setEntity({ id: "All", label: messages['clipRequestAdvancedFilters_all'] })
                  getData({})
                }}
              />
            </Grid>
          </Grid>
        </SidePanelHeader>
        <SidePanelBody>
          {/* <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            {
              entities.map((et, index) => {
                return (
                  <Grid item xs key={index}>
                    <Pill
                      key={index}
                      textField="label"
                      item={et}
                      outlined={et.id !== entity.id}
                      onClick={() => {
                        if (et.id == "All")
                          setFilters({ ...filters, assignedTo: [] })
                        else
                          setFilters({ ...filters, assignedTo: [{ id: -1, name: "All" }] })
                        setEntity(et);
                      }}
                      style={{ border: '0', width: '100%' }}
                      colorField="default"
                      colorSelection="005bf8"
                      isDisabled={loadingPills}
                    />
                  </Grid>
                )
              })
            }
          </Grid> */}
          <div style={{ marginTop: 20 }}>
            <TagsManage
              // height={250}
              value={filters.assignedTo || []}
              onChange={(item) => {
                console.log("value", item);
                onChangeEntityTagPicker(item);
              }}
              onlyTags={false}
              entities={entities}
              mode="selector"
              multiple={true}
              entitySelected={selectedEntity}
              setEntitySelected={setSelectedEntity}
              disableInheritance={false}
              menuStyle={{ maxHeight: '250px', maxWidth: '298px', overflowY: 'auto' }}
            />
          </div>

          <SeverityOptions
            label={messages['alertsNewAlert_SetSeverity']}
            onChange={(selectedSeverities) => setFilters({ ...filters, severities: selectedSeverities })}
            selectedButtons={filters.severities}
            messages={messages}
            multiple
          />

          <Select
            id="alertType"
            name="alertType"
            label={messages['alertsFiltersMenu_typeAlertFilter']}
            placeholder={messages['alertsFiltersMenu_typeAlertFilter']}
            value={filters.types}
            onChange={(values) => {
              setFilters({ ...filters, types: values })
            }}
            getOptionLabel={(item) => {
              const label = `${messages['alertsNewAlert_' + item.code]}`
              return label
            }}
            options={alertsSupported}
            textField={'name'}
            valueField={'code'}
            isSearchable
            isClearable
            multiple
          />
        </SidePanelBody>
        <SidePanelFooter>
          <Button
            className="btn-cancel"
            onClick={() => {
              setShowFilters(false);
            }}
          >
            {messages["cancel"]}
          </Button>

          <Button
            className="btn-blue"
            onClick={() => {
              getData()
              setFiltersByExport({
                ...filters,
                severities: filters?.severities?.map(x => x.id),
                assignedTo: filters?.assignedTo?.map(x => { return { id: x.id, name: x.name, label: x.label } }),
                types: filters?.types?.map(x => x.code)
              })
            }}
          >
            {messages["apply"]}
          </Button>
        </SidePanelFooter>
      </SidePanel>

      {/* <NewAlert /> */}
      <WizardAlert onResult={(refresh) => { refresh && getData(filters); }} />
      <DeleteTab pagination={pagination}/>

      {
        isOpenModalAssigned && <AssignedModal callBackSuscriber={() => {
          setRefreshSuscriber(true);
        }} />
      }

    </Container>
  );
};

export default AlersTab;
